import { useEffect } from 'react';
import { updateSEO, getSiteUrl } from '../../utils/seo';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = getSiteUrl();
    
    updateSEO({
      title: '会社概要 | おかやま介護認定調査株式会社 - 岡山市の介護認定調査専門企業',
      description: 'おかやま介護認定調査株式会社の会社概要。岡山市を中心に介護認定調査を専門に行う企業として、経験豊富な有資格者が一人ひとりの状況を丁寧に把握し、適切な介護サービスにつながる調査を実施しています。代表取締役：岡山太郎、従業員数：9名。',
      keywords: '会社概要,おかやま介護認定調査,岡山市,介護認定調査,企業情報,代表挨拶,アクセス',
      canonical: `${siteUrl}/about`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'AboutPage',
            '@id': `${siteUrl}/about#webpage`,
            url: `${siteUrl}/about`,
            name: '会社概要 | おかやま介護認定調査株式会社',
            description: 'おかやま介護認定調査株式会社の会社概要、代表挨拶、企業情報、アクセス情報をご紹介します。',
            isPartOf: {
              '@id': `${siteUrl}/#website`
            },
            about: {
              '@id': `${siteUrl}/#organization`
            }
          },
          {
            '@type': 'Organization',
            '@id': `${siteUrl}/#organization`,
            name: 'おかやま介護認定調査株式会社',
            url: siteUrl,
            foundingDate: '2026',
            numberOfEmployees: 9,
            founder: {
              '@type': 'Person',
              name: '岡山太郎',
              jobTitle: '代表取締役'
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: '○○町1-2-3',
              addressLocality: '岡山市北区',
              addressRegion: '岡山県',
              postalCode: '700-0000',
              addressCountry: 'JP'
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+81-86-012-3456',
              faxNumber: '+81-86-012-3457',
              contactType: 'customer service',
              areaServed: 'JP',
              availableLanguage: 'Japanese',
              hoursAvailable: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                opens: '09:00',
                closes: '18:00'
              }
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 34.66167,
              longitude: 133.88235
            }
          }
        ]
      }
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-[#F5F1E8] to-[#E8F4F4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 shadow-sm">
            <div className="w-2 h-2 bg-[#5D9B9B] rounded-full"></div>
            <span className="text-sm font-medium text-[#2C3E50]">ABOUT US</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C3E50] mb-6">
            会社概要
          </h1>
          <p className="text-lg sm:text-xl text-gray-700">
            誠実な調査で地域の介護を支えます
          </p>
        </div>
      </section>

      {/* CEO Message Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="sticky top-8">
                <div className="rounded-2xl overflow-hidden shadow-lg mb-6">
                  <img
                    src="https://public.readdy.ai/ai/img_res/aaec2057ee9c9f2940d6b6a44503aa4f.jpg"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>
                <p className="text-center text-lg font-medium text-[#2C3E50]">
                  代表取締役<br />
                  <span className="text-2xl font-bold">岡山 太郎</span>
                </p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="mb-6">
                <p className="text-sm font-medium text-[#5D9B9B] mb-2">代表挨拶</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50]">
                  地域に根ざした<br />介護認定調査を
                </h2>
              </div>

              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  おかやま介護認定調査株式会社のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                </p>
                <p>
                  私たちは、介護を必要とされる方々が適切なサービスを受けられるよう、正確で信頼性の高い介護認定調査を提供することを使命としています。介護認定調査は、その方の今後の生活を大きく左右する重要な手続きです。だからこそ、一人ひとりの状況を丁寧に把握し、心を込めて調査にあたることを大切にしています。
                </p>
                <p>
                  岡山市を中心とした地域で長年活動してきた経験から、地域の特性や介護環境を深く理解しています。この地域密着型のアプローチにより、迅速かつ適切な対応が可能となっています。
                </p>
                <p>
                  また、私たちのスタッフは全員が介護支援専門員などの有資格者であり、豊富な経験と専門知識を持っています。定期的な研修を通じて常に最新の知識を習得し、質の高いサービスを提供できるよう努めています。
                </p>
                <p>
                  これからも、地域の皆様に信頼される企業として、誠実に、丁寧に、介護認定調査を通じて地域社会に貢献してまいります。どうぞお気軽にご相談ください。
                </p>
                <p className="text-right italic text-xl font-medium text-[#2C3E50] mt-8">
                  代表取締役 岡山 太郎
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#F5F1E8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] text-center mb-12">
            会社情報
          </h2>

          <div className="mb-8 rounded-2xl overflow-hidden shadow-lg max-w-3xl mx-auto">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/e5b8705f4b622713ec762cf2a08eda5e.jpeg"
              alt="おかやま介護認定調査株式会社の外観"
              className="w-full h-auto object-cover object-top"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
            <div className="space-y-6">
              {[
                { label: '会社名', value: 'おかやま介護認定調査株式会社' },
                { label: '代表取締役', value: '岡山 太郎' },
                { label: '設立', value: '2026年' },
                { label: '従業員数', value: '9名' },
                { label: '所在地', value: '〒700-0000 岡山市北区○○町1-2-3' },
                { label: '電話番号', value: '086-012-3456' },
                { label: 'FAX', value: '086-012-3457' },
                { label: '営業時間', value: '9:00 〜 18:00' },
                { label: '定休日', value: '日曜日・祝日' },
                { label: '事業内容', value: '介護認定調査業務、介護保険に関するコンサルティング' },
                { label: '許認可・資格', value: '介護支援専門員、社会福祉士、介護福祉士' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-6 border-b border-gray-200 last:border-b-0"
                >
                  <div className="text-sm font-medium text-gray-500">
                    {item.label}
                  </div>
                  <div className="sm:col-span-2 text-base font-medium text-[#2C3E50]">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] text-center mb-12">
            アクセス
          </h2>

          <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64977654595!2d133.88235!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355408e8b3c2b3e9%3A0x7d6e7d7d7d7d7d7d!2z5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="おかやま介護認定調査株式会社の所在地"
            ></iframe>
          </div>

          <div className="text-center space-y-4">
            <p className="text-lg font-medium text-[#2C3E50]">
              〒700-0000 岡山市北区○○町1-2-3
            </p>
            <p className="text-gray-600">
              JR岡山駅から車で約15分<br />
              岡山ICから車で約20分<br />
              駐車場完備
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
